import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRadarData } from '../../redux/actions/certificateVerification';

const RadarChart = () => {
  const [animationProgress, setAnimationProgress] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 400, height: 400 });
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  
  // Sample data for preview
  const [data , setData] = useState([]);
  const certificateData = useSelector((store)=> store?.certificateVerification?.certificateData);
 let apiCall = 0;



  useEffect(()=>{
  if(certificateData?.unique_certificate_id && apiCall === 0){
    apiCall++;
    dispatch(getRadarData(certificateData?.unique_certificate_id, (successObj)=>{
      
      setData(successObj?.data);
    } , (errorObj)=>{}));

  }
  },[certificateData?.unique_certificate_id])

  // Intersection Observer to detect when component comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible && data && data.length > 0) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2, // Trigger when 20% of the component is visible
        rootMargin: '0px 0px -30px 0px' // Trigger slightly before fully visible
      }
    );

    if (containerRef.current && data && data.length > 0) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [isVisible, data]);

  // Handle responsive sizing - maximized
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // Maximize size - use almost full container width with minimal padding
        const maxSize = containerWidth - 20; // Only 20px padding total
        const minSize = 300;
        const size = Math.max(minSize, maxSize);
        setDimensions({ width: size, height: size });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Animation effect - only starts when component is visible and has data
  useEffect(() => {
    if (!isVisible || !data || data.length === 0) return; // Don't start animation until visible and data is available
    
    // Reset animation progress when starting
    setAnimationProgress(0);
    
    const duration = 2000; // 2 seconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setAnimationProgress(easeOut);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, data]);

  const size = Math.min(dimensions.width, dimensions.height);
  const center = size / 2;
  // Maximize hexagon size - increased from 24% to 40% of total size
  const maxRadius = size * 0.40; // Increased from 0.24
  // Extended radius for outer lines - increased from 36% to 45%
  const extendedRadius = size * 0.45; // Increased from 0.36
  
  // Calculate positions for 4 points on hexagon (top, top-right, bottom-right, bottom-left)
  const getPointPosition = (index, radius) => {
    const angles = [0, 60, 120, 240]; // Top, top-right, bottom-right, bottom-left
    const angle = (angles[index] - 90) * (Math.PI / 180); // Adjust to start from top
    return {
      x: center + Math.cos(angle) * radius,
      y: center + Math.sin(angle) * radius
    };
  };

  // Generate hexagon grid
  const getHexagonPoints = (radius) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i * 60 - 90) * (Math.PI / 180);
      points.push({
        x: center + Math.cos(angle) * radius,
        y: center + Math.sin(angle) * radius
      });
    }
    return points?.map(p => `${p.x},${p.y}`).join(' ');
  };

  // Generate hexagon grids - now dashed with updated color
  const hexagonGrids = [20, 40, 60, 80, 100]?.map(percent => (
    <polygon
      key={percent}
      points={getHexagonPoints((percent / 100) * maxRadius)}
      fill="none"
      stroke="#426FF4"
      strokeWidth="1"
      strokeDasharray="4,4"
      opacity="0.3"
    />
  ));

  // Generate all 6 axis lines for hexagon structure
  const axisLines = [];
  
  for (let i = 0; i < 6; i++) {
    const angle = (i * 60 - 90) * (Math.PI / 180);
    const extendedPoint = {
      x: center + Math.cos(angle) * extendedRadius,
      y: center + Math.sin(angle) * extendedRadius
    };
    
    // Main axis lines (solid) - full length from center to extended point
    axisLines.push(
      <line
        key={i}
        x1={center}
        y1={center}
        x2={extendedPoint.x}
        y2={extendedPoint.y}
        stroke="#426FF4"
        strokeWidth="1"
        opacity="0.4"
      />
    );
  }
  
  // Generate outer hexagon connecting lines (dashed)
  const outerHexagonLines = [];
  for (let i = 0; i < 6; i++) {
    const angle1 = (i * 60 - 90) * (Math.PI / 180);
    const angle2 = ((i + 1) * 60 - 90) * (Math.PI / 180);
    
    const point1 = {
      x: center + Math.cos(angle1) * extendedRadius,
      y: center + Math.sin(angle1) * extendedRadius
    };
    const point2 = {
      x: center + Math.cos(angle2) * extendedRadius,
      y: center + Math.sin(angle2) * extendedRadius
    };
    
    outerHexagonLines.push(
      <line
        key={`outer-${i}`}
        x1={point1.x}
        y1={point1.y}
        x2={point2.x}
        y2={point2.y}
        stroke="#426FF4"
        strokeWidth="1"
        strokeDasharray="4,4"
        opacity="0.3"
      />
    );
  }

  // Generate data points with animation
  const dataPoints = data?.map((item, index) => {
    const finalRadius = (item.score / 100) * maxRadius;
    const animatedRadius = finalRadius * animationProgress;
    return getPointPosition(index, animatedRadius);
  });

  // Create path for the data polygon
  const pathData = dataPoints?.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ') + ' Z';

  // Generate labels with gradient background - appearing after diamonds
  const labels = data?.map((item, index) => {
    const dataRadius = (data[index].score / 100) * maxRadius;
    const position = getPointPosition(index, dataRadius);
    const showLabels = animationProgress > 0.85; // Show labels after diamonds appear (reduced from 0.95)
    
    // Responsive label sizing - matching your specifications
    const labelPaddingX = 12; // px-3 equivalent
    const labelPaddingY = 8;  // py-2 equivalent
    const textWidth = item.skill_name.length * 7; // Approximate text width
    const labelWidth = textWidth + (labelPaddingX * 2);
    const labelHeight = 24; // Fixed height for consistent look
    const labelRadius = 8; // rounded-lg equivalent
    
    // Position labels directly under the diamond
    const xOffset = 0;
    const yOffset = size < 400 ? 28 : size < 600 ? 32 : 36;
    
    return (
      <g key={index}>
        {/* Gradient background rectangle */}
        <defs>
          <linearGradient id={`labelGradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4285F4" />
            <stop offset="76%" stopColor="#264D8E" />
          </linearGradient>
        </defs>
        <rect
          x={position.x + xOffset - labelWidth/2}
          y={position.y + yOffset - labelHeight/2}
          width={labelWidth}
          height={labelHeight}
          rx={labelRadius}
          ry={labelRadius}
          fill={`url(#labelGradient-${index})`}
          style={{
            opacity: showLabels ? 1 : 0,
            transform: showLabels ? 'scale(1)' : 'scale(0.8)',
            transition: 'opacity 0.5s ease-in, transform 0.5s ease-in',
            transitionDelay: `${index * 0.1}s`,
            transformOrigin: `${position.x + xOffset}px ${position.y + yOffset}px`
          }}
        />
        {/* White text - 12px font size */}
        <text
          x={position.x + xOffset}
          y={position.y + yOffset}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-white"
          style={{
            fontSize: '12px',
            fontWeight: '500', // font-medium
            opacity: showLabels ? 1 : 0,
            transition: 'opacity 0.5s ease-in',
            transitionDelay: `${index * 0.1 + 0.1}s`
          }}
        >
          {item.skill_name}
        </text>
      </g>
    );
  });

  // Diamond icon component - fixed 36px × 36px size
  const Diamond = ({ x, y, size: diamondSize = 36, opacity = 1 }) => {
    const fixedSize = 36; // Always 36px × 36px
    const scale = fixedSize / 36; // Scale factor is 1 (no scaling)
    
    return (
      <g transform={`translate(${x}, ${y}) scale(${scale})`} opacity={opacity}>
        {/* Custom SVG Icon */}
        <g transform="translate(-48, -48.5)"> {/* Center the 96x97 SVG */}
          <defs>
            <filter id={`filter0_d_${x}_${y}`} x="0" y="0.941406" width="96" height="96" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feOffset/>
              <feGaussianBlur stdDeviation="15"/>
              <feComposite in2="hardAlpha" operator="out"/>
              <feColorMatrix type="matrix" values="0 0 0 0 0.252148 0 0 0 0 0.416676 0 0 0 0 1 0 0 0 1 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
            </filter>
            <linearGradient id={`paint0_linear_${x}_${y}`} x1="27.5" y1="34.4414" x2="69.5" y2="59.9414" gradientUnits="userSpaceOnUse">
              <stop stopColor="#192E42"/>
              <stop offset="1" stopColor="white"/>
            </linearGradient>
          </defs>
          <g filter={`url(#filter0_d_${x}_${y})`}>
            <rect x="30" y="30.9414" width="36" height="36" rx="18" fill="#182C3F" shapeRendering="crispEdges"/>
            <rect x="31" y="31.9414" width="34" height="34" rx="17" stroke={`url(#paint0_linear_${x}_${y})`} strokeWidth="2" shapeRendering="crispEdges"/>
            <path d="M45.6727 46.2397L47.8753 41.9414H48.1247L50.3273 46.2397H45.6727ZM47.3766 55.9414L40.2078 47.4677H47.3766V55.9414ZM48.6234 55.9414V47.4677H55.7922L48.6234 55.9414ZM51.6987 46.2397L49.5377 41.9414H53.8182L56 46.2397H51.6987ZM40 46.2397L42.1818 41.9414H46.4623L44.3013 46.2397H40Z" fill="#F9D606"/>
          </g>
        </g>
      </g>
    );
  };

  // Loader component
  const Loader = () => (
    <div className="flex items-center justify-center w-full h-full min-h-[400px]">
      <div className="flex flex-col items-center space-y-4">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin"></div>
          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
        </div>
        <p className="text-gray-600 text-sm">Loading chart data...</p>
      </div>
    </div>
  );

  return (
   <div className="w-full">
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.1;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        
        @keyframes fadeInScale {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
      
      <div className="flex flex-col items-center w-full h-full">
        
        {/* Show loader when data is not present, show chart when data is available */}
        {!data || data.length === 0 ? (
          <Loader />
        ) : (
          <div ref={containerRef} className="w-full h-full">
            <div className="rounded-lg w-full h-full flex items-center justify-center">
              <div className="flex justify-center w-full">
                <svg 
                  width={size} 
                  height={size} 
                  viewBox={`0 0 ${size} ${size}`}
                  className="w-full h-auto"
                  style={{ maxHeight: '95vh', maxWidth: '95vw' }}
                >
                  {/* Hexagon grids (dashed) */}
                  {hexagonGrids}
                  
                  {/* Main axis lines (solid) */}
                  {axisLines}
                  
                  {/* Outer hexagon connecting lines (dashed) */}
                  {outerHexagonLines}
                  
                  {/* Data area with animation - updated colors */}
                  <path
                    d={pathData}
                    fill="rgba(66, 111, 244, 0.15)"
                    stroke="#426FF4"
                    strokeWidth="2"
                    strokeLinejoin="round"
                    style={{
                      transition: 'all 0.1s ease-out'
                    }}
                  />
                  
                  {/* Data points with staggered animation */}
                  {dataPoints?.map((point, index) => {
                    const delay = index * 0.15; // Stagger animation by 150ms per point
                    const pointProgress = Math.max(0, Math.min(1, (animationProgress - delay) / (1 - delay)));
                    const scale = pointProgress;
                    const showImage = animationProgress > 0.7; // Show images after main animation (reduced from 0.8)
                    
                    return (
                      <g key={index}>
                        {/* Animated circle that grows from center */}
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r="6"
                          fill="#426FF4"
                          stroke="white"
                          strokeWidth="2"
                          className="hover:scale-125 transition-transform cursor-pointer"
                          style={{
                            transform: `scale(${scale})`,
                            transformOrigin: `${point.x}px ${point.y}px`,
                            opacity: showImage ? 0 : pointProgress // Hide circle when image shows
                          }}
                        />
                        
                        {/* Pulse effect */}
                        {pointProgress > 0.8 && !showImage && (
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r="12"
                            fill="none"
                            stroke="#426FF4"
                            strokeWidth="1"
                            opacity="0.3"
                            style={{
                              animation: 'pulse 2s ease-out infinite'
                            }}
                          />
                        )}
                        
                        {/* Diamond placeholder image - appears after animation */}
                        {showImage && (
                          <g style={{
                            animation: 'fadeInScale 0.6s ease-out forwards',
                            animationDelay: `${index * 0.1}s`
                          }}>
                            <Diamond 
                              x={point.x} 
                              y={point.y} 
                              size={36}
                              opacity={1}
                            />
                          </g>
                        )}
                      </g>
                    );
                  })}
                  
                  {/* Labels */}
                  {labels}
                  
                  {/* Center point */}
                  <circle
                    cx={center}
                    cy={center}
                    r="3"
                    fill="#6b7280"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}
        
    
      </div>
    </div>
  );
};

export default RadarChart;